import { createInitialDesignHistoryState } from './designer-state';

export { createInitialDesignHistoryState };

export const HISTORY_KEYS = [
  'segments',
  'obstacles',
  'trees',
  'annotations',
  'setbackMode',
  'fireSetbacks'
];

export const DEFAULT_HISTORY_MAX_STEPS = 100;

export const DesignerHistoryCommandTypes = {
  UNDO: 'UNDO',
  REDO: 'REDO',
  SET_ENABLED: 'SET_ENABLED'
};

export function setDesignerHistoryEnabled(historyState, enabled) {
  return {
    ...historyState,
    state: enabled ? 'enabled' : 'disabled'
  };
}

export function stringifyHistoryValue(value) {
  if (value === undefined) return undefined;
  return typeof value === 'string' ? value : JSON.stringify(value);
}

export function parseHistoryValue(value) {
  if (value === undefined) return undefined;
  return typeof value === 'string' ? JSON.parse(value) : value;
}

export function buildHistorySnapshot(prevState, nextData, keys = null) {
  const targetKeys = keys || Object.keys(nextData || {});
  const previousHistoryItem = {};
  const historyItem = {};

  targetKeys.forEach((key) => {
    if (!HISTORY_KEYS.includes(key)) return;
    const oldValue = stringifyHistoryValue(prevState?.[key]);
    if (oldValue === undefined) return;
    const newValue = stringifyHistoryValue(nextData?.[key]);
    if (newValue === undefined) return;
    if (oldValue !== newValue) {
      previousHistoryItem[key] = oldValue;
      historyItem[key] = newValue;
    }
  });

  return { previousHistoryItem, historyItem, keys: targetKeys };
}

export function pushHistory(
  histories = [],
  historyState = createInitialDesignHistoryState(),
  snapshot = {}
) {
  const {
    previousHistoryItem = {},
    historyItem = {},
    keys = Object.keys(historyItem),
    maxSteps = DEFAULT_HISTORY_MAX_STEPS
  } = snapshot;

  const previousCount = Object.keys(previousHistoryItem).length;
  const currentCount = Object.keys(historyItem).length;
  if (!previousCount && !currentCount) {
    return {
      histories: [...histories],
      historyState: { ...historyState }
    };
  }

  const nextHistories = [...histories];
  const nextHistoryState = { ...historyState };
  const initHistory = nextHistories.length === 0;

  if (!initHistory) {
    const oldKeys = Object.keys(nextHistories[nextHistories.length - 1] || {});
    const mergeState = keys.some((key) => !oldKeys.includes(key));
    if (mergeState) {
      nextHistories[nextHistories.length - 1] = {
        ...nextHistories[nextHistories.length - 1],
        ...previousHistoryItem
      };
    }
  }

  const items = [];
  if (previousCount && initHistory) items.push({ ...previousHistoryItem });
  if (currentCount) items.push({ ...historyItem });

  if (items.length) {
    nextHistories.splice(nextHistoryState.current + 1, maxSteps, ...items);
    if (nextHistories.length > maxSteps) nextHistories.shift();
    nextHistoryState.current = nextHistories.length - 1;
  }

  return { histories: nextHistories, historyState: nextHistoryState };
}

export function undoHistory(
  histories = [],
  historyState = createInitialDesignHistoryState()
) {
  if (
    !histories.length ||
    historyState.current < 0 ||
    historyState.state === 'disabled'
  ) {
    return {
      histories,
      historyState,
      snapshot: null
    };
  }

  const current = Math.max(historyState.current - 1, 0);
  const nextHistoryState = { ...historyState, current };
  return {
    histories,
    historyState: nextHistoryState,
    snapshot: histories[current] || null
  };
}

export function redoHistory(
  histories = [],
  historyState = createInitialDesignHistoryState()
) {
  if (
    !histories.length ||
    historyState.current >= histories.length ||
    historyState.state === 'disabled'
  ) {
    return {
      histories,
      historyState,
      snapshot: null
    };
  }

  const current = Math.min(historyState.current + 1, histories.length - 1);
  const nextHistoryState = { ...historyState, current };
  return {
    histories,
    historyState: nextHistoryState,
    snapshot: histories[current] || null
  };
}

export function applyHistorySnapshot(state, snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return state;
  const data = {};
  Object.keys(snapshot).forEach((key) => {
    if (snapshot[key] !== undefined) {
      data[key] = parseHistoryValue(snapshot[key]);
    }
  });
  return { ...state, ...data };
}

export function runDesignerHistoryCommand(
  state,
  histories,
  historyState,
  command
) {
  if (command?.type === DesignerHistoryCommandTypes.SET_ENABLED) {
    return {
      state,
      histories,
      historyState: setDesignerHistoryEnabled(historyState, command.enabled),
      snapshot: null
    };
  }
  const historyResult =
    command?.type === DesignerHistoryCommandTypes.UNDO
      ? undoHistory(histories, historyState)
      : command?.type === DesignerHistoryCommandTypes.REDO
        ? redoHistory(histories, historyState)
        : { histories, historyState, snapshot: null };
  return {
    ...historyResult,
    state: applyHistorySnapshot(state, historyResult.snapshot)
  };
}
